import React, { useState, useEffect } from "react";
import {
  Input,
  IconButton,
  Text,
  Spinner,
  Textarea,
  useToast,
  useColorModeValue
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";

function EditableText({
  initialValue,
  onSave, // onSave now expected to be a promise that resolves to a boolean
  placeholder,
  pattern,
  invalid,
  editable = true,
  multiline = false,
  onCancel = async ()=>{},
  mode = "display",  //if it's "edit" it initializes in edit mode
  onModeChange = (_) => {},
  showPlaceholderInDisplayMode = true,
}) {
  const [editMode, setEditMode] = useState(mode === 'edit');
  const [value, setValue] = useState(initialValue);
  const [originalValue, setOriginalValue] = useState(initialValue); // Keep track of the original value
  const [isSaving, setIsSaving] = useState(false); // New state to track saving status
  const toast = useToast();
  const iconBackgroundColor = useColorModeValue( '#F1F1F5', '#303343');
  const iconColor = useColorModeValue( '#154043', '#F9F9FB');

  const handleSave = async () => {
    if (pattern && !pattern.test((value || '').trim())){
      if (invalid){
        toast({
          title: "Not a valid value",
          description: invalid,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }  
      return;
    }
    setIsSaving(true); // Indicate saving is in progress
    const saveResult = await onSave(value.trim());
    setIsSaving(false); // Reset saving indicator
    if (saveResult) {
      setOriginalValue(value.trim());
    } else {
      toast({
        title: "Not a valid value",
        description: invalid,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setValue(originalValue);
    }
    setEditMode(false);
  };

  useEffect(() => {
    // Update state when initialValue prop changes
    setValue(initialValue);
    setOriginalValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    setEditMode(mode === 'edit');
    onModeChange(mode === 'edit');
  }, [mode]);

  useEffect(() => {
    onModeChange(editMode);
  }, [editMode]);

  const handleCancel = async () => {
    setValue(originalValue);
    await onCancel();
    setEditMode(false);
  };

  /**
   * Conditionally return placeholder depending on showPlaceholderInDisplayMode
   * @returns {string}
   */
  const getPlaceholder = () => {
    if (!showPlaceholderInDisplayMode && !editMode) {
      return '';
    }

    return placeholder;
  };

  const EditComponent = multiline ? Textarea : Input;

  return (
    <>
      {editMode ? (
        <>
          <EditComponent
            as={multiline ? Textarea : Input} // Conditionally set the component
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={getPlaceholder()}
            size="sm"
            width="auto"
            flexGrow={1}
            isDisabled={isSaving} // Disable input while saving
          />
          {isSaving ? (
            <Spinner size="sm" ml={2} />
          ) : (
            <>
              <IconButton
                icon={<CheckIcon />}
                onClick={handleSave}
                size="sm"
                ml={2}
                aria-label="Save"
                isDisabled={isSaving}
                bg={iconBackgroundColor}
                color={iconColor}
              />
              <IconButton
                icon={<CloseIcon />}
                onClick={handleCancel}
                size="sm"
                ml={2}
                aria-label="Cancel"
                isDisabled={isSaving}
                bg={iconBackgroundColor}
                color={iconColor}
              />
            </>
          )}
        </>
      ) : (
        <>
          <Text
            as="span"
            flexGrow={1}
            onClick={() => editable && !isSaving && setEditMode(true)}
            textAlign="left"
            wordBreak="break-word"
          >
            {value !== undefined && value !== null ? value : getPlaceholder()}
          </Text>
          {editable && !isSaving && (
            <IconButton
              float="right"
              icon={<EditIcon />}
              onClick={() => setEditMode(true)}
              size="sm"
              ml={2}
              aria-label="Edit"
              bg={iconBackgroundColor}
              color={iconColor}
            />
          )}
        </>
      )}
    </>
  );
}

export default EditableText;
